import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { NewsletterCTA } from "@/components/site/NewsletterCTA";
import { CiteThis } from "@/components/site/CiteThis";
import { SUSPENSIONS, getSuspensionBySlug } from "@/lib/reference/suspensions";
import { SITE } from "@/lib/site";

export const Route = createFileRoute("/tools/suspensions/$slug")({
  loader: ({ params }) => {
    const s = getSuspensionBySlug(params.slug);
    if (!s) throw notFound();
    return s;
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [] };
    const url = `${SITE.url}/tools/suspensions/${params.slug}`;
    const title = `${loaderData.name} — GBP Suspension`;
    const desc = loaderData.description.slice(0, 158);
    return {
      meta: [
        { title: `${title} | ${SITE.name}` },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: `How to recover from ${loaderData.name}`,
            description: loaderData.description,
            url,
            step: loaderData.remediation.map((s, i) => ({
              "@type": "HowToStep",
              position: i + 1,
              text: s,
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.url}/` },
              { "@type": "ListItem", position: 2, name: "Tools", item: `${SITE.url}/tools` },
              { "@type": "ListItem", position: 3, name: "Suspensions", item: `${SITE.url}/tools/suspensions` },
              { "@type": "ListItem", position: 4, name: loaderData.name, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: SuspensionDetail,
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-2xl px-5 sm:px-8 py-24 text-center">
        <p className="eyebrow">Not found</p>
        <h1 className="headline-display mt-3 text-4xl">Suspension reason not indexed</h1>
        <Link to="/tools/suspensions" className="mt-6 inline-block underline">Back to suspensions</Link>
      </main>
      <SiteFooter />
    </div>
  ),
});

function SuspensionDetail() {
  const params = Route.useParams();
  const s = Route.useLoaderData();
  const url = `${SITE.url}/tools/suspensions/${params.slug}`;
  const others = SUSPENSIONS.filter((x) => x.slug !== s.slug);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <article className="mx-auto max-w-3xl px-5 sm:px-8 py-16">
        <nav aria-label="Breadcrumb" className="text-xs uppercase tracking-wider text-muted-foreground">
          <ol className="flex items-center gap-2 flex-wrap">
            <li><Link to="/" className="hover:text-accent">Home</Link></li>
            <li aria-hidden>/</li>
            <li><Link to="/tools" className="hover:text-accent">Tools</Link></li>
            <li aria-hidden>/</li>
            <li><Link to="/tools/suspensions" className="hover:text-accent">Suspensions</Link></li>
            <li aria-hidden>/</li>
            <li>{s.name}</li>
          </ol>
        </nav>
        <header className="mt-6">
          <p className="eyebrow">{s.severity} suspension</p>
          <h1 className="headline-display mt-3 text-5xl">{s.name}</h1>
        </header>

        <p className="mt-8 text-xl leading-relaxed font-serif text-foreground/90">{s.description}</p>

        <section className="mt-10">
          <h2 className="text-2xl font-semibold">Signals you're hit by this</h2>
          <ul className="mt-4 list-disc list-inside space-y-2 text-foreground/85">
            {s.signals.map((sig: string, i: number) => <li key={i}>{sig}</li>)}
          </ul>
        </section>

        <section className="mt-8">
          <h2 className="text-2xl font-semibold">Remediation</h2>
          <ol className="mt-4 list-decimal list-inside space-y-2 text-foreground/85">
            {s.remediation.map((r: string, i: number) => <li key={i} className="leading-relaxed">{r}</li>)}
          </ol>
        </section>

        <section className="mt-8 rounded-sm border border-border bg-paper-soft p-5">
          <p className="eyebrow">Prevention</p>
          <ul className="mt-2 list-disc list-inside space-y-1 text-sm">
            {s.prevention.map((p: string, i: number) => <li key={i}>{p}</li>)}
          </ul>
        </section>

        <section className="mt-10">
          <h2 className="text-2xl font-semibold">Other suspension reasons</h2>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2">
            {others.map((o) => (
              <li key={o.slug}>
                <Link
                  to="/tools/suspensions/$slug"
                  params={{ slug: o.slug }}
                  className="block rounded-sm border border-border bg-paper-soft px-3 py-2 text-sm hover:border-foreground transition-colors"
                >
                  {o.name}
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <div className="mt-12 flex items-center gap-3 border-t border-b border-border py-5">
          <CiteThis title={`${s.name} — GBP Suspension`} url={url} author={`${SITE.name} Newsroom`} />
          <p className="text-xs text-muted-foreground">Free to cite with a link back.</p>
        </div>

        <NewsletterCTA />
      </article>
      <SiteFooter />
    </div>
  );
}
